import React from "react";
import FAQ from "../../pages/Faq";

import { Helmet } from "react-helmet-async";

import {
  FaUser,
  FaEnvelopeOpenText,
  FaEnvelope,
  FaCaretRight,
  FaClipboardCheck,
  FaTriangleExclamation,
  FaShieldHalved,
  FaComments,
  FaGears,
  FaClipboardList,
  FaUserPen,
} from "react-icons/fa6";

import {
  FiClock,
  FiMail,
  FiFileText,
  FiSettings,
  FiCopy,
  FiTarget,
} from "react-icons/fi";
import { Check } from "lucide-react";
import WarningLetterGeneratorForm from "./WarningLetterGeneratorForm";
const steps = [
  {
    number: 1,
    title: "Enter Company Details",
    description:
      "Enter the company name, address and authorised signatory details.",
  },
  {
    number: 2,
    title: "Add Employee Information",
    description:
      "Add the employee's name, ID, designation, department and reporting manager.",
  },
  {
    number: 3,
    title: "Select the Warning Type",
    description: "Choose the warning level and category.",
  },
  {
    number: 4,
    title: "Describe the Incident",
    description: "Provide a factual explanation of what happened.",
  },
  {
    number: 5,
    title: "Mention the Expected Standard",
    description:
      "Explain the rule, policy or responsibility the employee was expected to follow.",
  },
  {
    number: 6,
    title: "Define Corrective Action",
    description: "State exactly what the employee must improve.",
  },
  {
    number: 7,
    title: "Add the Review Period",
    description: "Provide an improvement deadline and review date.",
  },
  {
    number: 8,
    title: "Generate and Review",
    description:
      "Generate the Warning Letter and verify all facts before issuing it.",
  },
];
const features = [
  <>
    <strong>First Written Warning:</strong> A first written warning formally
    documents an initial concern and provides the employee with an opportunity
    to improve.
  </>,
  <>
    <strong>Second Written Warning:</strong> A second warning may be issued when
    the same issue continues after the first warning.
  </>,
  <>
    <strong>Last Warning Letter:</strong> A last warning letter, also called a
    final written warning, communicates that the concern is serious or has
    continued despite earlier corrective action.
  </>,
  <>
    <strong>Show-Cause Warning:</strong> A show-cause warning asks the employee
    to explain the incident before the employer decides on further action.
  </>,
  <>
    <strong>Custom Warning:</strong> Employers may prepare a custom letter based
    on the organisation's disciplinary policy and the nature of the incident.
  </>,
];
const Faqs1 = [
  {
    title: "What Is a Warning Letter Used For?",
    content:
      "A warning letter is used to formally address issues such as misconduct, poor performance, attendance problems, negligence, or workplace policy violations.",
  },
  {
    title: "How Do I Write a Warning Letter to an Employee?",
    content:
      "Mention the specific issue, relevant date or incident, expected improvement, and any action the employee needs to take. Keep the wording factual and professional.",
  },
  {
    title: "Can I Create a Warning Letter for Poor Performance?",
    content:
      "Yes. A performance warning letter can document areas where an employee is not meeting expectations and clearly communicate the improvement required.",
  },
  {
    title: "Is a Warning Letter the Same as a Show-Cause Notice?",
    content:
      "No. A warning letter generally communicates a workplace concern and required corrective action. A show-cause notice asks the employee to explain an issue before further disciplinary action is considered.",
  },
  {
    title: "What Should Be Included in a Final Warning Letter?",
    content:
      "A final warning should clearly refer to the concern, previous warnings where relevant, expected corrective action, and the possible consequences of continued non-compliance.",
  },
  {
    title: "Can HR Create a Warning Letter Online?",
    content:
      "Yes. An online warning letter generator can help HR teams prepare a properly structured document without creating the format manually.",
  },
  {
    title: "Can I Download a Warning Letter as a PDF?",
    content:
      "Yes. After creating the warning letter, you can download the completed document as a PDF and use it for employee communication and HR documentation.",
  },
];

const schema = [
  // =========================================================
  // SCHEMA 1: WebApplication
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Waggex Free Warning Letter Generator",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Free online warning letter generator. Enter company details, employee information, incident details, policy reference, and corrective action to create and download a professional warning letter PDF instantly.",
    url: "https://www.waggex.com/hr-documents/warning-letter-generator",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    featureList: [
      "Free warning letter generator India",
      "Online warning letter format India",
      "Warning letter PDF download",
      "Employee disciplinary warning letter template India",
      "Professional warning letter maker India",
      "Warning letter for poor performance",
      "Warning letter for attendance issues",
      "Employee misconduct warning letter",
    ],
  },

  // =========================================================
  // SCHEMA 2: FAQPage
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What Is a Warning Letter Used For?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A warning letter is used to formally address issues such as misconduct, poor performance, attendance problems, negligence, or workplace policy violations.",
        },
      },
      {
        "@type": "Question",
        name: "How Do I Write a Warning Letter to an Employee?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Mention the specific issue, relevant date or incident, expected improvement, and any action the employee needs to take. Keep the wording factual, clear, and professional.",
        },
      },
      {
        "@type": "Question",
        name: "Can I Create a Warning Letter for Poor Performance?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. A performance warning letter can document areas where an employee is not meeting expectations and clearly communicate the improvement required.",
        },
      },
      {
        "@type": "Question",
        name: "Can a Warning Letter Be Used for Attendance Issues?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. A warning letter can address repeated absenteeism, late coming, unauthorised leave, or other attendance-related concerns that require formal communication.",
        },
      },
      {
        "@type": "Question",
        name: "What Is a Final Warning Letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A final warning letter is used when a serious issue occurs or previous warnings have not resulted in improvement. It communicates that further violations may lead to additional disciplinary action.",
        },
      },
      {
        "@type": "Question",
        name: "Can I Create a Warning Letter Online for Free?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex provides a free online warning letter generator that helps you create a professional employee warning letter without preparing the document format from scratch.",
        },
      },
      {
        "@type": "Question",
        name: "Can I Download a Warning Letter as a PDF?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. After creating the warning letter, you can download the completed document as a PDF for employee communication and HR records.",
        },
      },
    ],
  },

  // =========================================================
  // SCHEMA 3: Organization
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides free HR document generation tools and HRMS software for Indian businesses.",
    areaServed: "IN",
  },
];

const warningLetterContent = [
  {
    icon: FaUser,
    title: "Employee Identification",
    description:
      "Includes the employee's name, designation, ID, department, and reporting manager.",
  },
  {
    icon: FaClipboardCheck,
    title: "Warning Classification",
    description:
      "Defines the warning level, category, and severity of the disciplinary concern.",
  },
  {
    icon: FaTriangleExclamation,
    title: "Incident Details",
    description:
      "Explains what happened, including the incident date, time, location & relevant facts.",
  },
  {
    icon: FaShieldHalved,
    title: "Policy or Standard Violated",
    description:
      "Identifies the company policy, workplace rule, responsibility, or expected standard involved.",
  },
  {
    icon: FaComments,
    title: "Previous Communication",
    description:
      "Records any earlier counselling, discussions, verbal warnings, or written notices issued to the employee.",
  },
  {
    icon: FaGears,
    title: "Required Corrective Action",
    description:
      "Clearly states the behaviour, performance, or conduct improvement expected from the employee.",
  },
  {
    icon: FaClipboardList,
    title: "Improvement and Review Period",
    description:
      "Specifies the improvement deadline, monitoring period & scheduled review date.",
  },
  {
    icon: FaUserPen,
    title: "Employee Response",
    description:
      "Explains the possible next disciplinary action and allows the employee to provide an explanation or acknowledgement.",
  },
];
const relievingLetterPoints = [
  "Describe the Issue",
  "Mention the Incident Date",
  "Identify the Policy",
  "Explain Required Improvement",
  "Set an Improvement Deadline",
  "State Possible Consequencess",
];

const companystepsFeatures = [
  {
    id: 1,
    icon: <FiClock />,
    title: "Save HR Time",
    desc: "Generate structured warning letters without drafting every document manually.",
  },
  {
    id: 2,
    icon: <FiMail />,
    title: "Maintain a Standard Format",
    desc: "Use a consistent warning letter format for similar workplace concerns.",
  },
  {
    id: 3,
    icon: <FiFileText />,
    title: "Record Facts Clearly",
    desc: "Structured fields help document dates, incidents, evidence and expectations.",
  },
  {
    id: 4,
    icon: <FiSettings />,
    title: "Explain Required Improvement",
    desc: "Clearly communicate what the employee must correct.",
  },
  {
    id: 5,
    icon: <FiCopy />,
    title: "Maintain Disciplinary Records",
    desc: "Keep formal records of warnings, responses and review periods.",
  },
  {
    id: 6,
    icon: <FiTarget />,
    title: "Reduce Unclear Communication",
    desc: "Avoid vague language by stating the concern and corrective action directly.",
  },
];
const noticePoints = [
  "Explanation Requested",
  "Incident Details",
  "Response Deadline",
  "Employee Statement",
  "Disciplinary Review",
];

const warningPoints = [
  "Formal Concern Notice",
  "Incident Details",
  "Required Improvement",
  "Correction Deadline",
  "Possible Consequences",
];
export default function WarningLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>Free Warning Letter Generator | Create & Download PDF</title>
        <meta
          name="description"
          content="Create a professional employee warning letter online for free. Add employee details, misconduct, and corrective action, then download your PDF"
        />
        <meta
          name="keywords"
          content="warning letter generator online,warning letter format India,free warning letter maker online India,employee warning letter template India,warning letter PDF generator India,written warning letter format India,misconduct warning letter India,warning letter Word format India,disciplinary warning letter India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/hr-documents/warning-letter-generator"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <div className="px-2 py-3 sm:px-4">
        <section className="rounded-xl border border-white px-2 sm:px-5 py-7 shadow-sm backdrop-blur bg-[#ffffff40]">
          <div className="text-center mb-10">
            <h1 className="text-[#10324F] text-lg sm:text-3xl font-roboto font-bold leading-snug mb-4">
              Free Warning Letter Generator Online
            </h1>

            <p className="mx-auto mt-8 max-w-5xl text-sm sm:text-lg leading-6 text-[#10324F] min-h-18 sm:min-h-12">
              Create a professional employee warning letter for misconduct,
              attendance issues, poor performance, negligence, or workplace
              policy violations
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row mb-10">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
              >
                Start Free Trial
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
              >
                Book a Demo
              </button>
            </div>
          </div>
          <WarningLetterGeneratorForm />
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ">
                {/* Left Content */}
                <div>
                  <div className="mt-4">
                    <div className="mb-6 flex items-center gap-3">
                      <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                      <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                        WHAT IS A WARNING LETTER?
                      </span>
                    </div>

                    <h2 className="text-xl md:text-3xl font-semibold text-[#10324F] mt-3 mb-5">
                      Create Clear, Professional Employee Warnings
                    </h2>

                    <p className="text-[#10324F] mb-6 leading-relaxed max-w-2xl">
                      Prepare a formal warning letter for attendance issues,
                      misconduct, poor performance, negligence, or policy
                      violations. Keep the communication clear and professional
                      while documenting the concern for proper HR records
                    </p>
                  </div>
                </div>

                {/* Right Content */}
                <div className="flex flex-col">
                  {relievingLetterPoints.map((item, index) => (
                    <React.Fragment key={item}>
                      <div className="relative z-10 flex min-h-12.5 items-center justify-center rounded-lg border border-white  bg-linear-to-b from-white to-[#D1DFEA]/80  hover:bg-white/90 px-4 py-3 text-center text-sm font-medium text-[#10324F] shadow-sm backdrop-blur-sm transition sm:min-h-12.5 sm:text-base">
                        {item}
                      </div>

                      {index < relievingLetterPoints.length - 1 && (
                        <div className="mx-auto h-6 border-l border-dashed border-[#10324F]/80" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  WHAT DOES THE WARNING LETTER FORMAT INCLUDE?
                </p>
                <h2 className="mt-4 text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                  Everything You Need for a Professional Warning Letter
                </h2>

                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Create a complete warning letter with employee details,
                  incident or concern, applicable policy, required corrective
                  action, response timeline, and authorised signatory details
                </p>
              </div>

              {/* Cards */}
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {warningLetterContent.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="group flex min-h-25.5 items-start gap-4 rounded-xl border border-white/90 bg-linear-to-b from-white/60 to-[#D1DFEA]/70  hover:bg-white/90 px-5 py-5 transition-all duration-200 shadow-sm"
                    >
                      {/* Icon */}
                      <div className="flex shrink-0 items-center justify-center pt-1">
                        <Icon className="text-2xl text-[#10324F]" />
                      </div>

                      {/* Content */}
                      <div>
                        <h3 className="text-sm sm:text-lg font-semibold leading-5 text-[#10324F]">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-xs sm:text-sm leading-5 text-[#10324F]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  HOW TO CREATE A WARNING LETTER ONLINE
                </p>

                <h2 className="mt-4 text-xl font-bold leading-tight text-[#10324F] sm:text-3xl">
                  Create Your Warning Letter in a Few Easy Steps
                </h2>
              </div>

              {/* Steps */}

              {/* ROW 1 */}
              <div className=" mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(0, 4).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE */}
                    <div className="absolute left-0 right-1/2 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#10324F] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#1324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ROW 2 */}
              <div className=" mt-4 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(4, 8).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE — IMPORTANT FOR STEP 5 */}
                    <div className="absolute left-0 right-1/2 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#163957] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#10324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-8 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-8">
                {/* LEFT CONTENT */}
                <div>
                  {/* Small heading */}

                  <div className="mb-6 flex items-center gap-3">
                    <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                      UNDERSTANDING THE DIFFERENCE
                    </span>
                  </div>

                  {/* Main heading */}
                  <h2 className="text-xl font-semibold leading-tight text-[#10324F] sm:text-3xl">
                    Warning Letter or Show-Cause Notice: Which One Do You Need?
                  </h2>

                  {/* Intro */}
                  <p className="mt-7 max-w-2xl text-sm leading-8 text-[#10324F]">
                    Choose the right document based on the stage of employee
                    disciplinary action. A warning letter addresses an
                    established concern, while a show-cause notice seeks an
                    explanation before further action
                  </p>
                  <div className="py-4 max-w-xl">
                    <div className="flex flex-col gap-2">
                      {/* Salary Increment Letter */}
                      <div>
                        <h2 className="text-sm font-semibold text-[#10324F] mb-2">
                          Warning Letter
                        </h2>
                        <ul className="list-none p-0 m-0 space-y-2">
                          <li className="text-sm leading-relaxed text-[#10324F] pl-6 relative before:content-['•'] before:absolute before:left-0 before:text-[#10324F] before:font-bold before:text-lg">
                            Use when the concern has been reviewed and the
                            employee needs to correct their conduct,
                            performance, attendance, or policy violation
                          </li>
                          <li className="text-sm leading-relaxed text-[#10324F] pl-6 relative before:content-['•'] before:absolute before:left-0 before:text-[#10324F] before:font-bold before:text-lg">
                            Clearly state the issue and the corrective action
                            expected
                          </li>
                        </ul>
                      </div>

                      {/* Salary Revision Letter */}
                      <div>
                        <h2 className="text-sm font-semibold text-[#10324F] mb-2">
                          Show-Cause Notice
                        </h2>
                        <ul className="list-none p-0 m-0 space-y-2">
                          <li className="text-sm leading-relaxed text-[#10324F] pl-6 relative before:content-['•'] before:absolute before:left-0 before:text-[#10324F] before:font-bold before:text-lg">
                            Use when the employee needs to explain their actions
                            or respond to an allegation before the organisation
                            decides on further disciplinary action
                          </li>
                          <li className="text-sm leading-relaxed text-[#10324F] pl-6 relative before:content-['•'] before:absolute before:left-0 before:text-[#10324F] before:font-bold before:text-lg">
                            Give the employee a clear opportunity to respond
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT COMPARISON */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* ================= OFFER LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelopeOpenText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Warning Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b  from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {warningPoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ================= APPOINTMENT LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelope className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Show-Cause Notice
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {noticePoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHY USE AN ONLINE WARNING LETTER FORMAT?
                </p>
                <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F]  uppercase">
                  Create Warning Letters And Download Free PDF
                </h2>
              </div>
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4">
                {companystepsFeatures.map((item) => {
                  // const Icon = item.icon;

                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/90  bg-linear-to-b from-white/60 to-[#D1DFEA]/60  hover:bg-white/30 p-4 duration-200 shadow-sm transition-all"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-center mb-5">
                        {/* <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                          {item.id}
                        </div> */}
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10324F] text-xl text-white">
                          {item.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <h2 className="text-center text-sm sm:text-base font-semibold text-[#10324F] font-roboto">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-center text-sm leading-6 text-[#10324F] font-roboto">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto mb-4">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  TYPES OF EMPLOYEE WARNING LETTERS
                </p>
                <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
                  Choose the Right Warning Level for the Situation
                </h2>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 place-items-start">
                {features.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-[#10324F] px-2 py-3  rounded-2xl"
                  >
                    {/* Check Icon */}
                    <div className="mt-1 flex items-center justify-center text-[#10324F]">
                      <Check size={30} aria-hidden="true" />
                    </div>

                    {/* Text */}
                    <p className="font-roboto text-sm  font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <FAQ
            title="Frequently Asked Question"
            items={Faqs1}
            defaultOpenIndex={0}
          />
          <section className="w-full bg-[#F4F6F8]/60 px-6 py-10 rounded-xl border border-white mt-8">
            <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold uppercase">
                Create Your Warning Letter Instantly
              </h2>

              <p className="mt-4 text-[#10324F] text-sm sm:text-base">
                reate a professional employee warning letter with the key
                incident and corrective action details. Generate it free, no
                login required, and download the PDF instantly.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3
              rounded-xl
              bg-[#10324F]
              text-white text-xs sm:text-sm
              font-medium
              shadow-md
              transition-all duration-300
              hover:scale-105
            "
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  Generate Warning Letter
                </button>

                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3 text-xs sm:text-sm
              rounded-xl
              border border-[#10324F]
              text-[#10324F]
              font-medium
              transition-all duration-300
              hover:bg-white
              hover:text-[#16385C]
            "
                  onClick={() => (window.location.href = "/all-hr-documents")}
                >
                  Explore more
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
